// stockcontrol-frontend/src/components/auth/configuracion/Configuracion.tsx
"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { api } from "../../../services/Axios"
import { useAuth } from "../../../services/AuthContext"
import Loader from "../../utils/loader/Loader"
import ConfirmationModal from "../../utils/avisos/ConfirmationModal"
import OrganizationDetails from "./OrganizationDetails"
import MessageModal from "../../utils/avisos/MessageModal"
import AddOrEditVendedorModal from "./VendedorModal"
import {
  UserPlus,
  Edit,
  Trash2,
  Save,
  Users
} from "lucide-react"
import Button from "../../ui/Button"
import { Card, CardHeader, CardTitle, CardContent } from "../../ui/Card"

export interface Organization {
  _id?: string
  name?: string
  razonSocial?: string
  NIT?: string
  direccion?: string
  ciudad?: string
  telefonoContacto?: string
  responsableIVA?: boolean
  createdAt?: string
  identificador?: string
  pin?: string
  setId?: string
}

interface User {
  nombre: string
  email: string
  role: string
}

const Configuracion: React.FC = () => {
  const { userData, updateUserData } = useAuth()
  const [vendedores, setVendedores] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false)
  const [confirmationAction, setConfirmationAction] = useState<"update" | "delete" | null>(null)
  const [isMessageOpen, setIsMessageOpen] = useState<boolean>(false)
  const [message, setMessage] = useState<string>("")
  const [messageType, setMessageType] = useState<"success" | "error">("success")
  const [isAddOrEditModalOpen, setIsAddOrEditModalOpen] = useState<boolean>(false)
  const [vendedorToEdit, setVendedorToEdit] = useState<User | null>(null)
  const [vendedorToDelete, setVendedorToDelete] = useState<string | null>(null)
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);

  const [organizationFields, setOrganizationFields] = useState<Organization>(userData?.organization || {})

  useEffect(() => {
    const loadVendedores = async () => {
      if (userData?.role === "admin" && userData.organization?._id) {
        try {
          const [vendedoresRes, subscriptionRes] = await Promise.all([
            api.get(`/api/auth/users?organization=${userData.organization._id}`),
            api.get(`/api/subscriptions`),
          ]);
          setVendedores(vendedoresRes.data);
          setSubscriptionPlan(subscriptionRes.data?.subscription?.plan || null);
        } catch (error) {
          console.error("Error al cargar datos:", error);
        }
      }
      setIsLoading(false);
    };
  
    if (userData) {
      setOrganizationFields(userData.organization || {});
      loadVendedores();
    }
  }, [userData]);

  const normalizarPlan = (plan: string): string =>
    plan.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/plan\s+/i, "").trim();

  const cajerosPorPlan: Record<string, number> = {
    basico: 0,
    estandar: 1,
    premium: 5,
  };

  const validateOrganizationFields = () => {
    if (!organizationFields.razonSocial || !organizationFields.direccion) {
      setMessage("Rellene los campos obligatorios.")
      setMessageType("error")
      setIsMessageOpen(true)
      return false
    }
    return true
  }

  const confirmUpdateOrganization = async () => {
    if (!validateOrganizationFields()) return
    setIsConfirmationOpen(false)
    try {
      const response = await api.put("/api/auth/organization/update", organizationFields)
      const updatedOrganization: Organization = { ...userData?.organization, ...organizationFields }
      updateUserData({ organization: updatedOrganization })
      setMessage(response.data.msg)
      setMessageType("success")
    } catch (error) {
      setMessage("Error al actualizar la organización.")
      setMessageType("error")
    } finally {
      setIsMessageOpen(true)
    }
  }

  const handleOpenAddVendedorModal = () => {
    setVendedorToEdit(null)
    setIsAddOrEditModalOpen(true)
  }

  const handleOpenEditVendedorModal = (vendedor: User) => {
    setVendedorToEdit(vendedor)
    setIsAddOrEditModalOpen(true)
  }

  const handleVendedorSaved = () => {
    setIsAddOrEditModalOpen(false)
    if (userData?.role === "admin" && userData.organization?._id) {
      api
        .get(`/api/auth/users?organization=${userData.organization._id}`)
        .then((response) => setVendedores(response.data))
        .catch((error) => console.error("Error al recargar los vendedores:", error))
    }
  }

  const confirmDeleteVendedor = (email: string) => {
    setVendedorToDelete(email)
    setConfirmationAction("delete")
    setIsConfirmationOpen(true)
  }

  const handleDeleteVendedor = async () => {
    if (vendedorToDelete) {
      try {
        await api.delete(`/api/auth/user/delete-vendedor?email=${vendedorToDelete}`)
        setVendedores(vendedores.filter((vendedor) => vendedor.email !== vendedorToDelete))
        setMessage("Vendedor eliminado exitosamente")
        setMessageType("success")
      } catch (error) {
        console.error("Error al eliminar el vendedor:", error)
        setMessage("Error al eliminar el vendedor")
        setMessageType("error")
      } finally {
        setVendedorToDelete(null)
        setIsConfirmationOpen(false)
        setIsMessageOpen(true)
      }
    }
  }

  const closeMessageModal = () => {
    setIsMessageOpen(false)
  }

  const handleFieldChange = (field: keyof Organization, value: any) => {
    setOrganizationFields((prev) => ({ ...prev, [field]: value }))
  }

  const handleConfirmation = () => {
    if (confirmationAction === "update") {
      confirmUpdateOrganization()
    } else if (confirmationAction === "delete") {
      handleDeleteVendedor()
    }
    setConfirmationAction(null)
  }

  if (isLoading) return <Loader />

  return (
    <div className="min-h-screen bg-gradient-to-b from-white px-4 py-10">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Configuración</h1>
          <div className="w-20 h-1 bg-[#2d53da] mx-auto rounded-full"></div>
        </div>

        <Card className="mb-8 shadow-lg border-0">
          <CardHeader className="bg-[#2d53da] text-white">
            <CardTitle className="text-xl text-white">Información de la Organización</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <OrganizationDetails organizationFields={organizationFields} handleFieldChange={handleFieldChange} />
            <div className="mt-6 flex justify-end">
              <Button
                onClick={() => {
                  setConfirmationAction("update")
                  setIsConfirmationOpen(true)
                }}
                icon={<Save />}
                className="bg-[#2d53da] hover:bg-[#2d53da]/90 text-white"
            >
                Actualizar Organización
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0 overflow-hidden">
          <CardHeader className="bg-[#2d53da] text-white flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center text-xl text-white">
              <Users className="mr-2" /> Lista de Cajeros
            </CardTitle>
            <Button
              icon={<UserPlus />}
              onClick={handleOpenAddVendedorModal}
              disabled={
                subscriptionPlan !== null &&
                vendedores.length >= (cajerosPorPlan[normalizarPlan(subscriptionPlan)] ?? 0)
              }
              className="bg-white !text-[#2d53da] hover:bg-gray-100"
            >
              Agregar Cajero
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              {vendedores.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                        Rol
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {vendedores.map((vendedor) => (
                      <tr key={vendedor.email} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0 bg-[#2d53da]/10 rounded-full flex items-center justify-center">
                              <span className="text-[#2d53da] font-medium">
                                {vendedor.nombre.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{vendedor.nombre}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{vendedor.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            {vendedor.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <Button
                              onClick={() => handleOpenEditVendedorModal(vendedor)}
                              variant="outline"
                              size="sm"
                              icon={<Edit />}
                              className="border-[#2d53da] text-[#2d53da] hover:bg-[#2d53da]/10"
                            >
                              Editar
                            </Button>
                            <Button
                              onClick={() => confirmDeleteVendedor(vendedor.email)}
                              variant="danger"
                              size="sm"
                              icon={<Trash2 />}
                            >
                              Eliminar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <div className="h-20 w-20 bg-[#2d53da]/10 rounded-full flex items-center justify-center mb-4">
                    <UserPlus className="h-10 w-10 text-[#2d53da]" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    {subscriptionPlan?.toLowerCase() === "básico" || cajerosPorPlan[normalizarPlan(subscriptionPlan || "")] === 0
                      ? "Su plan actual no permite registrar cajeros"
                      : "No hay cajeros registrados"}
                  </h3>

                  <p className="text-gray-500 max-w-md mb-6">
                    {subscriptionPlan?.toLowerCase() === "básico" || cajerosPorPlan[normalizarPlan(subscriptionPlan || "")] === 0
                      ? "Actualice su plan para habilitar esta funcionalidad."
                      : "Agregue un cajero para ayudar en la operación diaria de su negocio."}
                  </p>

                  <Button
                    onClick={handleOpenAddVendedorModal}
                    icon={<UserPlus />}
                    disabled={
                      subscriptionPlan === null ||
                      vendedores.length >= (cajerosPorPlan[normalizarPlan(subscriptionPlan)] ?? 0)
                    }
                    className={`${
                      vendedores.length >= (cajerosPorPlan[normalizarPlan(subscriptionPlan || "")] ?? 0)
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-[#2d53da] hover:bg-[#2d53da]/90 text-white"
                    }`}
                  >
                    {subscriptionPlan?.toLowerCase() === "básico" || cajerosPorPlan[normalizarPlan(subscriptionPlan || "")] === 0
                      ? "Su suscripción no tiene habilitada esta opción"
                      : "Agregar Cajero"}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <AddOrEditVendedorModal
          isOpen={isAddOrEditModalOpen}
          onClose={() => setIsAddOrEditModalOpen(false)}
          onVendedorSaved={handleVendedorSaved}
          vendedorToEdit={vendedorToEdit}
        />

        <ConfirmationModal
          isOpen={isConfirmationOpen}
          title="Confirmación Requerida"
          message="¿Está seguro de que desea continuar con esta acción?"
          onConfirm={handleConfirmation}
          onCancel={() => setIsConfirmationOpen(false)}
        />

        <MessageModal
          isOpen={isMessageOpen}
          message={message}
          messageType={messageType}
          onClose={closeMessageModal}
        />
      </div>
    </div>
  )
}

export default Configuracion